/**
 * Typed client for the new-product launch flow (PR 17, plan O —
 * backend/api/launch_api.py, prefix /api/launches). The Launch tab on both
 * product pages, the Launching chip's data and the Pending changes tray's
 * "are listing updates switched on" read are its only callers.
 *
 * Money and Amazon: nothing here changes Amazon. A listing draft and the House
 * launch campaigns are QUEUED in Pending changes and sent only when someone
 * applies them there (behind the server's listing / campaign switches and
 * manual writes). "Check with Amazon" and "Check status on Amazon" only READ.
 * Pulling Jungle Scout keywords costs Jungle Scout quota, not money, and the
 * server caps it to one pull per launch every ten minutes. Finding competitors
 * is a read-only Amazon catalog search the server caps and caches.
 *
 * Reads go through `apiGet`; the writes through `apiRequest` (lib/api.ts `req`)
 * because two of them are PUTs, which `apiSend` does not take.
 */
import { apiGet, apiRequest } from './api'
import { isApiError } from './query/apiError'

export type LaunchPhase = 'pre_launch' | 'launch' | 'graduated'
export type LaunchStatus = 'active' | 'done' | 'cancelled'
export type TaskStatus = 'done' | 'open' | 'n/a'
export type StageStatus = 'done' | 'in_progress' | 'not_started'

/** What this server can do for a launch. Booleans and one automation row. */
export type LaunchServerStatus = {
  listing_writes: boolean
  manual_writes: boolean
  junglescout: boolean
  sp_api: boolean
  automation: LaunchAutomation | null
}

export type LaunchAutomation = {
  id: number
  /** 'off' | 'watching' | 'approve' (Ask me first) | 'live'. */
  status: string
  name: string | null
}

/** One launch as a list row and as the head of the detail. */
export type LaunchCore = {
  id: number
  uid: string
  asin: string | null
  product_name: string | null
  brand: string | null
  image_url: string | null
  status: LaunchStatus
  active: boolean
  stage: number
  stage_label: string
  owner: string | null
  notes: string | null
  launch_date: string | null
  phase: LaunchPhase
  graduated_at: string | null
  done_at: string | null
  created_at: string | null
  updated_at: string | null
}

export type LaunchTask = {
  code: string
  stage: number
  label: string
  status: TaskStatus
  /** True when ClickPay decides it; a person cannot tick it. */
  auto: boolean
  /** Why an automatic task is still open ("No UPC yet"). */
  hint: string | null
  owner: string | null
  note: string | null
  done_by: string | null
  done_at: string | null
}

export type LaunchStageCard = {
  n: number
  label: string
  status: StageStatus
  tasks: LaunchTask[]
}

export type RecordComponent = { component_key: string; label: string; qty: number; source: string }

export type RecordFacts = {
  product_name: string | null
  brand: string | null
  upc_ean: string | null
  pack_size: string | null
  pack_family: string | null
  unit_cost: number | null
  landed_unit_cost: number | null
  age_min_months: number | null
  cpsia_warning: string | null
  batteries_required: boolean | null
  batteries_included: boolean | null
  battery_type: string | null
  battery_count: number | null
  hazmat_regulation: string | null
  components: RecordComponent[]
  dims: Record<string, number | null>
  /** Plain labels of what still needs an answer. */
  missing: string[]
}

export type LaunchKeyword = {
  keyword: string
  /** Any of 'bank' | 'sibling' | 'junglescout' | 'manual'. */
  sources: string[]
  search_volume: number | null
  sibling_orders: number | null
  sibling_asin: string | null
  competitor_asin: string | null
  /** 1..10 when one of the primary 10, else null. */
  primary_rank: number | null
  pulled_at: string | null
}

export type KeywordsPayload = {
  items: LaunchKeyword[]
  competitor_asins: string[]
  last_pull_at: string | null
}

export type ResearchResult = {
  pulled: number
  /** Why nothing was pulled ("Jungle Scout isn't set up on this server"). */
  skipped_reason: string | null
  items: LaunchKeyword[]
  /** The ASINs the pull used (the typed ones, or the ones ClickPay found). */
  competitor_asins: string[]
  /** True when nothing was typed and ClickPay's own suggestions were used. */
  auto_found: boolean
}

export type CompetitorSuggestion = {
  asin: string
  title: string | null
  brand: string | null
  sales_rank: number | null
  rank_category: string | null
  image_url: string | null
  /** The search that found it. */
  query: string
  /** 'Found by searching "stress cube sensory"'. */
  reason: string
}
export type CompetitorsPayload = {
  items: CompetitorSuggestion[]
  reason: string | null
  cached_at: string | null
  queries: string[]
}

export type GateLevel = 'block' | 'warn'
export type Gate = { code: string; ok: boolean; level: GateLevel; message: string }

export type PreviewIssue = { code: string; severity: string; attributes: string[]; message: string }

export type ListingCopy = {
  title: string
  highlight: string
  bullets: string[]
  description: string
  backend_keywords: string
  included_components: string
  price: number | null
  fulfillment: 'fba' | 'fbm' | null
  fbm_quantity: number | null
  color: string
  style: string
  material: string
  safety_warning: string
  item_type_keyword: string
  extra_attributes: Record<string, unknown>
}

export type DraftStatus = 'draft' | 'staged' | 'sent' | 'not_sent' | 'accepted' | 'failed' | 'superseded'

export type AmazonRefresh = {
  asin: string | null
  status: string[]
  buyable: boolean
  checked_at?: string
}

export type ListingDraft = {
  id: number
  sku: string
  product_type: string
  requirements: string | null
  mode: 'create' | 'edit'
  template_sku: string | null
  asin: string | null
  copy: ListingCopy
  gates: Gate[]
  preview_status: 'VALID' | 'INVALID' | 'ERROR' | null
  preview_issues: PreviewIssue[]
  preview_at: string | null
  status: DraftStatus
  edit_id: number | null
  sent_at: string | null
  submission: Record<string, unknown> | null
  error_message: string | null
  refresh: AmazonRefresh | null
}

export type ListingSibling = { sku: string; asin: string | null; product_type: string | null; title: string | null }

export type ListingPayload = {
  draft: ListingDraft | null
  siblings: ListingSibling[]
  product_types: string[]
  asin: string | null
}

export type ListingSaveBody = {
  sku: string
  product_type: string
  mode: 'create' | 'edit'
  requirements?: string | null
  template_sku?: string | null
  copy: Partial<ListingCopy>
}

export type ListingScore = {
  overall: number
  length_score: number
  coverage_score: number
  compliance_score: number
  readability_score: number
  issues: string[]
  suggestions: string[]
  /** The house rule that wins over the score's own title advice. */
  note: string
}

/** The Listing Agent's rewrite; nothing is saved until the person applies it. */
export type ListingRewrite = {
  rewrites: { title?: string; bullets?: string[]; description?: string; backend_keywords?: string }
  /** Problems the rewrite itself would run into (banned words …). */
  compliance_check: string[]
  /** Which primary keywords the rewrite kept. */
  preserved_terms: string[]
  note?: string
}

export type PreviewResult = { preview_status: 'VALID' | 'INVALID' | 'ERROR'; issues: PreviewIssue[]; preview_at: string }
export type StageResult = { draft_id: number; edit_id: number; staged_at: string | null; sendable: boolean }
export type RefreshResult = { asin: string | null; status: string[]; buyable: boolean; issues: PreviewIssue[]; message?: string }

export type PpcCampaignRow = {
  launch_id: number
  name: string | null
  /** 'staged' | 'created' | 'not_sent' | 'failed' | 'creating'. */
  status: string
  status_label: string
  campaign_id: string | null
  edit_id: number | null
}

export type PpcBlock = {
  batches: { batch_id: string; campaigns: PpcCampaignRow[] }[]
  launch_date: string | null
  tagged: { campaigns: number; keywords: number }
  automation: LaunchAutomation | null
}

/** A product event (backend/product_events/model.py row_to_api), as the Watch list shows it. */
export type WatchEvent = {
  id: number
  type: string
  subtype: string | null
  /** Account day, YYYY-MM-DD. */
  day: string
  title: string
  source_label: string
  actor: string | null
}

export type WatchSummary = {
  launch_date: string | null
  day_n: number | null
  ends_on: string | null
  phase: LaunchPhase
  graduated_at: string | null
  ads: {
    impressions_30d: number | null
    clicks_30d: number | null
    spend_30d: number | null
    sales_30d: number | null
    orders_30d: number | null
  }
  events: WatchEvent[]
  buyable: boolean
}

export type LaunchDetail = {
  launch: LaunchCore
  stages: LaunchStageCard[]
  record: RecordFacts
  keywords: { seed_count: number; primary_count: number; primary: string[]; competitor_asins: string[]; last_pull_at: string | null; competitor_suggested_at: string | null }
  listing: ListingDraft | null
  images: { approved_slots: string[]; pending_patch_status: string | null }
  ppc: PpcBlock
  watch: WatchSummary
}

export type LaunchListItem = LaunchCore & { open_tasks: number }

/** The New campaign page's House launch starting config (ppc/preset). */
export type LaunchPreset = {
  preset: 'house_launch'
  asins: { asin: string; sku: string }[]
  exact_keywords: string[]
  discovery_keywords: string[]
  product_targets: string[]
  profile_id: string | null
  portfolio_id: string | null
  budgets: HouseBudgets
  default_bid: number
  start_paused: boolean
  start_date: string
  naming: { separator: string; tokens: { kind: string; text?: string }[] }
  url: string
}

export type HouseBudgets = { auto: number | null; exact: number | null; discovery: number | null; pat: number | null }

const enc = encodeURIComponent
const body = (operator: string | null | undefined, rest: Record<string, unknown> = {}) =>
  JSON.stringify({ ...rest, operator_slug: operator ?? null })
const send = <T>(method: 'POST' | 'PATCH' | 'PUT', path: string, payload: string) =>
  apiRequest<T>(path, { method, body: payload })

export const launchApi = {
  status: () => apiGet<LaunchServerStatus>('/api/launches/status'),

  list: (status: LaunchStatus | 'all' = 'active') =>
    apiGet<{ items: LaunchListItem[]; count: number }>(`/api/launches?status=${status}`),

  /** The launch of a product, or null when it has none (the server answers 404). */
  byUid: async (uid: string): Promise<LaunchDetail | null> => {
    try {
      return await apiGet<LaunchDetail>(`/api/launches/by-uid/${enc(uid)}`)
    } catch (e) {
      if (isApiError(e) && e.status === 404) return null
      throw e
    }
  },

  get: (id: number) => apiGet<LaunchDetail>(`/api/launches/${id}`),

  start: (uid: string, operator: string | null, owner?: string | null) =>
    send<LaunchDetail>('POST', '/api/launches', body(operator, { uid, owner: owner ?? null })),

  patch: (id: number, operator: string | null, fields: { owner?: string | null; notes?: string | null; status?: LaunchStatus; launch_date?: string }) =>
    send<LaunchDetail>('PATCH', `/api/launches/${id}`, body(operator, fields)),

  patchTask: (id: number, code: string, operator: string | null, fields: { done?: boolean; not_applicable?: boolean; owner?: string | null; note?: string | null }) =>
    send<LaunchTask>('PATCH', `/api/launches/${id}/tasks/${enc(code)}`, body(operator, fields)),

  keywords: (id: number, refresh = false) =>
    apiGet<KeywordsPayload>(`/api/launches/${id}/keywords${refresh ? '?refresh=1' : ''}`),

  competitors: (id: number, refresh = false) =>
    apiGet<CompetitorsPayload>(`/api/launches/${id}/keywords/competitors${refresh ? '?refresh=1' : ''}`),

  addKeyword: (id: number, operator: string | null, keyword: string) =>
    send<KeywordsPayload>('POST', `/api/launches/${id}/keywords`, body(operator, { keyword })),

  research: (id: number, operator: string | null, competitorAsins: string[]) =>
    send<ResearchResult>('POST', `/api/launches/${id}/keywords/research`, body(operator, { competitor_asins: competitorAsins })),

  setPrimary: (id: number, operator: string | null, keywords: string[]) =>
    send<{ primary: string[] }>('PUT', `/api/launches/${id}/keywords/primary`, body(operator, { keywords })),

  listing: (id: number) => apiGet<ListingPayload>(`/api/launches/${id}/listing`),

  productTypes: (id: number, q: string) =>
    apiGet<{ items: { name: string; display_name?: string }[] }>(`/api/launches/${id}/listing/product-types?q=${enc(q)}`),

  saveListing: (id: number, operator: string | null, b: ListingSaveBody) =>
    send<ListingDraft>('PUT', `/api/launches/${id}/listing`, body(operator, { ...b })),

  score: (id: number, operator: string | null) =>
    send<ListingScore>('POST', `/api/launches/${id}/listing/score`, body(operator)),

  rewrite: (id: number, operator: string | null, issues?: string[]) =>
    send<ListingRewrite>('POST', `/api/launches/${id}/listing/rewrite`, body(operator, issues ? { issues } : {})),

  preview: (id: number, operator: string | null) =>
    send<PreviewResult>('POST', `/api/launches/${id}/listing/preview`, body(operator)),

  stage: (id: number, operator: string | null) =>
    send<StageResult>('POST', `/api/launches/${id}/listing/stage`, body(operator)),

  refreshListing: (id: number, operator: string | null) =>
    send<RefreshResult>('POST', `/api/launches/${id}/listing/refresh`, body(operator)),

  ppcPreset: (id: number) => apiGet<LaunchPreset>(`/api/launches/${id}/ppc/preset`),

  setLaunchDate: (id: number, operator: string | null, launchDate?: string) =>
    send<{ launch_date: string; tagged: { campaigns: number; keywords: number } }>(
      'POST', `/api/launches/${id}/ppc/launch-date`, body(operator, launchDate ? { launch_date: launchDate } : {})),

  watch: (id: number) => apiGet<WatchSummary>(`/api/launches/${id}/watch`),
}
